import { X } from 'lucide-react';
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../button/button';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Optional icon component to be displayed on the left side of the input */
  leftIcon?: ReactNode;
  /** Whether the input should show a clear button */
  clearable?: boolean;
  /** Callback function triggered when the clear button is clicked */
  onClear?: () => void;
  /** Whether the input should be right aligned */
  rightAlign?: boolean;
};

/**
 * Input component that extends the native HTML input with additional features.
 *
 * @component
 * @example
 * ```tsx
 * <Input
 *   leftIcon={<SearchIcon />}
 *   clearable
 *   onClear={() => setValue('')}
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 * />
 * ```
 *
 * @param {InputProps} props - The component props
 * @param {ReactNode} [props.leftIcon] - Optional icon component displayed on the left side
 * @param {boolean} [props.clearable=false] - Whether to show a clear button
 * @param {() => void} [props.onClear] - Callback function when clear button is clicked
 * @param {string} [props.className] - Additional CSS classes to apply
 * @param {string} [props.type] - Input type (text, password, etc.)
 * @param {boolean} [props.rightAlign=false] - Whether to right align the input
 *
 * @returns A styled input component with optional left icon and clear button
 */

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftIcon, clearable = false, onClear, rightAlign = false, ...props }, ref) => {
    return (
      <div className="relative flex items-center">
        {leftIcon && <div className="absolute left-3 flex h-4 w-4 items-center text-seapoint-gray">{leftIcon}</div>}

        <input
          type={type}
          className={cn(
            'file:text-foreground flex min-h-8 w-full rounded-3xl border border-seapoint-gray-dark bg-transparent px-3 py-2 text-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:font-medium placeholder:text-seapoint-gray focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            leftIcon && 'pl-9',
            rightAlign && 'text-right',
            clearable && 'pr-10',
            className,
          )}
          ref={ref}
          {...props}
        />

        {clearable && onClear && (
          <Button variant="ghost" className="absolute right-0 flex items-center text-seapoint-gray" onClick={onClear}>
            <X size={16} />
          </Button>
        )}
      </div>
    );
  },
);
